'use client';

import { useTranslations } from 'next-intl';
import { Cookie, Shield, BarChart3, Settings } from 'lucide-react';
import LegalPageLayout from '@/components/legal/LegalPageLayout';
import { ConsentSettings } from '@/components/legal/ConsentSettings';

export default function Cookies() {
  const t = useTranslations();

  return (
    <LegalPageLayout
      titleKey="legal.cookies.title"
      subtitleKey="legal.cookies.subtitle"
      icon={Cookie}
      iconColor="text-blue-600"
    >
      <div id="settings" className="mb-12 rounded-2xl border border-blue-200 bg-blue-50 p-8">
        <div className="mb-4 flex items-center gap-3">
          <Settings className="h-6 w-6 text-blue-600" />
          <h2 className="m-0 text-2xl font-bold text-gray-950">
            {t('legal.cookies.manageTitle')}
          </h2>
        </div>
        <p className="mb-6 text-blue-900">{t('legal.cookies.manageIntro')}</p>
        <ConsentSettings />
      </div>

      <h2>{t('legal.cookies.whatAreCookies.title')}</h2>
      <p>{t('legal.cookies.whatAreCookies.text')}</p>

      <h2>{t('legal.cookies.howWeUse.title')}</h2>
      <p>{t('legal.cookies.howWeUse.text')}</p>

      <div className="mb-12 grid gap-6 md:grid-cols-3">
        <div className="rounded-2xl bg-gray-50 p-6 text-center">
          <Shield className="mx-auto mb-4 h-12 w-12 text-green-600" />
          <h3 className="mb-3 text-lg font-semibold text-gray-950">
            {t('legal.cookies.types.necessary.title')}
          </h3>
          <p className="mb-4 text-sm text-green-700">
            {t('legal.cookies.types.necessary.description')}
          </p>
          <ul className="space-y-1 text-left text-sm text-green-700">
            {(t.raw('legal.cookies.types.necessary.items') as string[]).map((item, index) => (
              <li key={index}>• {item}</li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl bg-gray-50 p-6 text-center">
          <BarChart3 className="mx-auto mb-4 h-12 w-12 text-blue-600" />
          <h3 className="mb-3 text-lg font-semibold text-gray-950">
            {t('legal.cookies.types.analytics.title')}
          </h3>
          <p className="mb-4 text-sm text-blue-700">
            {t('legal.cookies.types.analytics.description')}
          </p>
          <ul className="space-y-1 text-left text-sm text-blue-700">
            {(t.raw('legal.cookies.types.analytics.items') as string[]).map((item, index) => (
              <li key={index}>• {item}</li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl bg-gray-50 p-6 text-center">
          <Settings className="mx-auto mb-4 h-12 w-12 text-purple-600" />
          <h3 className="mb-3 text-lg font-semibold text-gray-950">
            {t('legal.cookies.types.marketing.title')}
          </h3>
          <p className="mb-4 text-sm text-purple-700">
            {t('legal.cookies.types.marketing.description')}
          </p>
          <ul className="space-y-1 text-left text-sm text-purple-700">
            {(t.raw('legal.cookies.types.marketing.items') as string[]).map((item, index) => (
              <li key={index}>• {item}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mb-8 rounded-2xl bg-gray-50 p-8">
        <h2 className="mb-4 text-2xl font-semibold text-gray-950">
          {t('legal.cookies.contact.title')}
        </h2>
        <p className="mb-4 text-blue-700">{t('legal.cookies.contact.text')}</p>
        <div className="rounded-2xl border border-gray-200/80 bg-white p-4">
          <p className="text-gray-700">
            <strong>{t('legal.company.name')}</strong>
            <br />
            E-Mail:{' '}
            <a
              href={`mailto:${t('legal.company.email')}`}
              className="text-blue-600 hover:underline"
            >
              {t('legal.company.email')}
            </a>
            <br />
            Telefon:{' '}
            <a href={`tel:${t('legal.company.phone')}`} className="text-blue-600 hover:underline">
              {t('legal.company.phone')}
            </a>
            <br />
            Adresse: {t('legal.company.address')}, {t('legal.company.city')},{' '}
            {t('legal.company.country')}
          </p>
        </div>
      </div>

      <div className="rounded-2xl bg-gray-100 p-6 text-center">
        <Cookie className="mx-auto mb-3 h-8 w-8 text-gray-600" />
        <p className="text-sm text-gray-600">
          {t('legal.cookies.lastUpdated', { date: new Date().toLocaleDateString() })}
        </p>
      </div>
    </LegalPageLayout>
  );
}
